import React, { useState, useEffect } from 'react';
import { Loader2, Plane, Ship, Clock, Calculator, Info, BatteryCharging, MessageCircle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { currencyMeta } from '../lib/currency';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v, code) => {
  const m = currencyMeta(code);
  return `${m.symbol}${Number(v).toLocaleString('en-US', { minimumFractionDigits: m.decimals, maximumFractionDigits: m.decimals })}`;
};

export default function FreightEstimator({ currency, onBook, booking, bookingResult }) {
  const [notes, setNotes] = useState('');
  const [opts, setOpts] = useState(null);
  const [category, setCategory] = useState('Other / General Cargo');
  const [amount, setAmount] = useState('1');
  const [destination, setDestination] = useState('Ghana');
  const [method, setMethod] = useState('sea_lcl');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const methodCfg = opts?.methods?.find((m) => m.key === method) || { unit_label: 'CBM', rate_usd: 210, step: 0.1, min: 0.1 };
  const liveTotal = Number(amount) > 0 ? Number(amount) * methodCfg.rate_usd : 0;

  useEffect(() => {
    fetch(`${API}/api/sourcing/freight-options`).then((r) => r.json())
      .then((d) => setOpts(d))
      .catch(() => toast.error('Could not load estimator options'));
  }, []);

  useEffect(() => {
    if (result) run(currency); // re-price in the newly selected currency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const changeMethod = (key) => {
    setMethod(key);
    setResult(null);
    const next = opts?.methods?.find((m) => m.key === key);
    if (next && Number(amount) < next.min) setAmount(String(next.min));
  };

  const run = async (cur = currency, e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sourcing/freight-estimate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, amount: Number(amount), destination, method, currency: cur }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Estimate failed');
      setResult(data.data);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const isAir = method.startsWith('air');
  const sel = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white text-gray-900 focus:border-orange-500 focus:outline-none';

  return (
    <div className="space-y-4" data-testid="freight-estimator">
      <form onSubmit={(e) => run(currency, e)} className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr_1fr_1.3fr_auto] gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Product Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={sel} data-testid="est-category">
            {(opts?.categories || ['Other / General Cargo']).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block" data-testid="est-amount-label">
            {isAir ? 'Weight (kg)' : 'Volume (CBM)'} <span className="text-gray-400">· ${methodCfg.rate_usd}/{methodCfg.unit_label}</span>
          </label>
          <div className="relative">
            <input type="number" inputMode="decimal" min={methodCfg.min} step={methodCfg.step} value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={isAir ? 'e.g. 25' : 'e.g. 0.3'} className={`${sel} pr-14`} required data-testid="est-amount" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500" data-testid="est-unit">{methodCfg.unit_label}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Destination Country</label>
          <select value={destination} onChange={(e) => setDestination(e.target.value)} className={sel} data-testid="est-destination">
            {(opts?.destinations || ['Ghana']).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Shipping Method</label>
          <select value={method} onChange={(e) => changeMethod(e.target.value)} className={sel} data-testid="est-method">
            {(opts?.methods || [{ key: 'sea_lcl', label: 'Sea Freight — LCL', rate_usd: 210, unit_label: 'CBM' }]).map((m) => (
              <option key={m.key} value={m.key}>{m.label} — ${m.rate_usd}/{m.unit_label}</option>
            ))}
          </select>
        </div>
        <Button type="submit" disabled={loading || !(Number(amount) > 0)} className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl px-5 py-2.5 w-full md:w-auto" data-testid="est-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}<span className="ml-2 md:hidden">Calculate freight</span>
        </Button>
      </form>

      <p className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="est-live-preview">
        <Info className="w-3.5 h-3.5 text-orange-500" />
        {Number(amount) > 0
          ? <>{Number(amount)} {methodCfg.unit_label} × ${methodCfg.rate_usd}/{methodCfg.unit_label} = <strong className="text-gray-800">${liveTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</strong> — press calculate to see it in {currency} and the delivery time.</>
          : <>Enter your {isAir ? 'weight in kg' : 'volume in CBM (e.g. 0.1, 0.2, 0.3…)'} to see the freight cost.</>}
      </p>

      {result && (
        <div className="bg-gradient-to-br from-blue-950 to-blue-900 text-white rounded-2xl p-5 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-5" data-testid="est-result">
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-300 font-semibold">Estimated freight cost · {result.method_label}</p>
            <p className="text-3xl sm:text-4xl font-extrabold text-orange-400 mt-2 leading-tight" data-testid="est-freight-usd">
              ${result.freight_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-base font-semibold text-blue-200">USD</span>
            </p>
            {result.freight_fx !== null && result.freight_fx !== undefined && result.currency !== 'USD' && (
              <p className="text-lg font-bold text-emerald-300 mt-1" data-testid="est-freight-fx">≈ {fmtMoney(result.freight_fx, result.currency)} {result.currency}</p>
            )}
            <p className="text-sm text-blue-200 mt-1">≈ ¥{result.freight_rmb.toLocaleString()} RMB · {result.basis}</p>

            <div className="mt-4 flex items-center gap-3 bg-white/10 rounded-xl p-3" data-testid="est-lead-time">
              {result.method === 'air_battery' ? <BatteryCharging className="w-6 h-6 text-orange-400" /> : isAir ? <Plane className="w-6 h-6 text-orange-400" /> : <Ship className="w-6 h-6 text-orange-400" />}
              <div>
                <p className="text-xs text-blue-300 uppercase font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Estimated delivery to {result.destination}</p>
                <p className="font-bold text-lg">{result.lead_time_label}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-xl p-4 text-sm space-y-1.5" data-testid="est-breakdown">
            <p className="text-xs uppercase tracking-widest text-blue-300 font-semibold mb-2">Details</p>
            <div className="flex justify-between"><span className="text-blue-200">Category</span><span className="text-right">{result.category}</span></div>
            <div className="flex justify-between"><span className="text-blue-200">{result.unit === 'kg' ? 'Weight' : 'Volume'}</span><span>{result.amount} {result.unit_label}</span></div>
            <div className="flex justify-between"><span className="text-blue-200">Rate</span><span>${result.rate_usd}/{result.unit_label}</span></div>
            <div className="flex justify-between font-bold border-t border-white/10 pt-2"><span className="text-blue-100">Freight total</span><span>${result.freight_usd.toLocaleString()}</span></div>
            <div className="pt-2 text-xs text-blue-300 flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Shanghai → {result.destination}. Freight only — product cost, local duties and clearance are quoted separately by your agent.</div>
          </div>

          {onBook && (
            <div className="md:col-span-2 border-t border-white/10 pt-4" data-testid="est-book-section">
              {bookingResult ? (
                <div className="bg-emerald-500/15 border border-emerald-400/40 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="est-book-success">
                  <CheckCircle2 className="w-8 h-8 text-emerald-300 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold">Booking request #{bookingResult.booking_id} sent to your agent</p>
                    <p className="text-xs text-blue-200">If WhatsApp didn't open, tap the button to confirm with your agent.</p>
                  </div>
                  <a href={bookingResult.whatsapp_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-4 py-2.5 rounded-xl" data-testid="est-book-whatsapp-link"><MessageCircle className="w-4 h-4" /> Open WhatsApp</a>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for your agent (pickup date, supplier, packaging...)"
                    className="flex-1 px-3 py-2.5 rounded-xl text-sm bg-white/10 border border-white/20 text-white placeholder-blue-300 focus:outline-none focus:border-orange-400" data-testid="est-book-notes" />
                  <Button type="button" onClick={() => onBook({ category, amount: Number(amount), destination, method, currency, notes })} disabled={booking}
                    className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl px-5 py-2.5" data-testid="est-book-btn">
                    {booking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />} Book this shipment via WhatsApp
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
